import { HdLpsColumn } from './hd-planet-display-config';

export type HdUndefinedActivationStyle = 'opacity-border' | 'corner-notch' | 'hatch-overlay' | 'hatch-notch-border' | 'hatch-border' | 'notch-border' | 'border-only' | 'inverse' | 'inverse-border' | 'inverse-border-hatch' | 'none';
export type HdNodeBackgroundStyle = 'side-color' | 'classic-red';

/**
 * Visual style for the "beneath the line" Color/Tone blocks.
 * - `modern`  — circle + triangle, white digits, no border.
 * - `classic` — hexagon + triangle, both with a blue border, black digits with white outline.
 */
export type HdBeneathTheLineStyle = 'modern' | 'classic';

/** Horizontal alignment for the gate.line(.color) number label inside a planet activation block. */
export type HdGateNumberAlign = 'left' | 'right';

/**
 * Font-size variant for the gate.line(.color) number label.
 * - `large`  — gate 26px / line & color 18px (default).
 * - `medium` — gate 20px / line & color 16px (more compact, gate stays larger than line/color).
 */
export type HdGateNumberFontSize = 'large' | 'medium';

/**
 * Orientation in which the Base badge quadrants are rendered.
 * - `classic` — original "Base Orientation" mapping (Personality Mat: 1=BL, 3=TL;
 *   Design Mat: 1=TL, 5=BL). Matches the historical printed charts.
 * - `lps`     — the entire badge is conceptually rotated 90° counter-clockwise so
 *   that the mapping aligns with the Lunar Planetary Square layout. The number
 *   stays upright; only the quadrant positions shift (TL→BL, BL→BR, BR→TR, TR→TL).
 *   Default.
 */
export type HdBaseRotation = 'classic' | 'lps';

/**
 * Color configuration for the planet grid panels.
 * These extend the bodygraph color config and can be swapped per theme.
 */
export interface HdPlanetGridColorConfig {
  /** Background for the incarnation cross planets (Sun/Earth) per side. */
  readonly incarnationCross: {
    readonly personality: string;
    readonly design: string;
  };

  /**
   * Tekst op de incarnatiekruis-blokken: `'light'` (wit, standaard) voor een
   * donkere achtergrond zoals het paars, `'dark'` voor een lichte achtergrond
   * zoals het amber van Whale. Ontbreekt in oudere opgeslagen configs,
   * vandaar optioneel; `undefined` = `'light'`.
   */
  readonly incarnationCrossTextTone?: 'light' | 'dark';

  /** Background for the life cross planets (Nodes) per side. */
  readonly lifeCross: {
    readonly personality: string;
    readonly design: string;
  };

  /** Background per LPS column — determines the blue shade of Moon-Pluto blocks. */
  readonly lpsColumn: Readonly<Record<HdLpsColumn, string>>;

  /** Extra-light background for Moon (special case in LPS prana column). */
  readonly lpsMoon: string;

  /** Slightly darker/desaturated background for Moon when a highlight is active. */
  readonly lpsMoonHighlighted: string;

  /** Background for the Color/Tone blocks (Motivation & View). */
  readonly colorToneBlock: string;

  /**
   * Colors for the split Color (circle/hexagon) and Tone (triangle) shapes.
   * The shape is divided in two halves: a `primary` (blue) half and a side-specific half.
   * Vertical split (Color): values 1-3 → primary on top, values 4-6 → primary on bottom.
   * Horizontal split (Tone): values 1-3 → primary on right, values 4-6 → primary on left.
   */
  readonly colorToneShape: {
    readonly primary: string;
    readonly personality: string;
    readonly design: string;
    /** Digit color used in the `modern` style. */
    readonly numberColor: string;
    /** Digit color used in the `classic` style. */
    readonly classicNumberColor: string;
    /** Outline color drawn around the digit in the `classic` style. */
    readonly classicNumberOutlineColor: string;
    /** Border color drawn around the shapes in the `classic` style. */
    readonly borderColor: string;
  };

  /** Visual variant for the Color/Tone (beneath-the-line) blocks. */
  readonly beneathTheLineStyle: HdBeneathTheLineStyle;

  /** Horizontal alignment of the gate.line(.color) number label in each planet block. */
  readonly gateNumberAlign: HdGateNumberAlign;

  /** Font-size variant for the gate.line(.color) number label in each planet block. */
  readonly gateNumberFontSize: HdGateNumberFontSize;

  /**
   * Rotation applied to the Base badge quadrants. See {@link HdBaseRotation}.
   * Defaults to `'lps'` so the badge aligns with the Lunar Planetary Square layout.
   */
  readonly baseRotation: HdBaseRotation;

  /** Undefined gate styling options. */
  readonly undefinedStyle: HdUndefinedActivationStyle;
  readonly undefinedOpacity: number;
  readonly undefinedBackgroundColor: string;
  readonly undefinedInnerBorderColor: string;
  readonly undefinedNotchColor: string;
  readonly undefinedHatchColor: string;

  /** Node background styling options. */
  readonly nodeBackgroundStyle: HdNodeBackgroundStyle;

  /** Exaltation indicator color. */
  readonly exaltation: string;

  /** Exaltation arrow color. */
  readonly exaltationArrow: string;

  /** Detriment indicator color. */
  readonly detriment: string;

  /** Detriment arrow color. */
  readonly detrimentArrow: string;

  /** Base badge background colors per variant. */
  readonly baseBadge: {
    readonly defined: string;
    readonly undefined: string;
    /** Red color used for filled quadrants in the base badge. */
    readonly quadrantColor: string;
  };

  /** Line number highlight color (the .lijn color in cross blocks). */
  readonly lineHighlight: {
    readonly personality: string;
    readonly design: string;
  };

  /**
   * Highlight color for LPS planet line numbers that match the opposite-side node's line.
   * Configured separately so personality-node matches and design-node matches can differ.
   */
  readonly nodeMatchLineHighlight: {
    /** Color for design-side LPS planets whose line matches the personality node's line. */
    readonly personality: string;
    /** Color for personality-side LPS planets whose line matches the design node's line. */
    readonly design: string;
  };

  /** Color number highlight color (the .kleur color in LPS blocks). */
  readonly colorHighlight: {
    readonly personality: string;
    readonly design: string;
  };

  /** Darker variant of color highlights for light backgrounds. */
  readonly colorHighlightDark: {
    readonly personality: string;
    readonly design: string;
  };

  /** Darker variants of line highlights for light backgrounds (like Moon block). */
  readonly lineHighlightDark: {
    readonly personality: string;
    readonly design: string;
  };

  /** Darker variants of node match highlights for light backgrounds. */
  readonly nodeMatchLineHighlightDark: {
    readonly personality: string;
    readonly design: string;
  };
}

export type HdPlanetGridColorConfigPresetId = 'classic' | 'warm-dusk-subtle' | 'warm-dusk' | 'whales-of-personality';

export interface HdPlanetGridColorConfigPreset {
  id: HdPlanetGridColorConfigPresetId;
  label: string;
  config: Readonly<HdPlanetGridColorConfig>;
}

export const HD_PLANET_GRID_CLASSIC_COLOR_CONFIG: Readonly<HdPlanetGridColorConfig> = {
  incarnationCross: {
    personality: 'rgb(168, 56, 195)', // #a838c3 — purple
    design: 'rgb(168, 56, 195)' // #a838c3 — purple (now same as personality)
  },
  lifeCross: {
    personality: 'rgb(14, 14, 14)', // #0e0e0e — black
    design: 'rgb(206, 58, 58)' // #ce3a3a — red
  },
  lpsColumn: {
    presence: 'rgb(1, 90, 173)', // #015aad — dark blue
    prana: 'rgb(29, 134, 231)', // #1d86e7 — medium blue
    vertical: 'rgb(1, 90, 173)' // #015aad — dark blue
  },
  lpsMoon: 'rgb(60, 160, 240)', // #3ca0f0 — slightly lighter version of Uranus blue
  lpsMoonHighlighted: 'rgb(113 189 255)', // #71BDFF — even lighter for highlighted moon
  colorToneBlock: 'rgb(204, 237, 255)', // #ccedff — light blue
  colorToneShape: {
    primary: 'rgb(33, 141, 240)', // #218df0 — blue (Figma reference)
    personality: 'rgb(14, 14, 14)', // #0e0e0e — black (matches lifeCross.personality)
    design: 'rgb(206, 58, 58)', // #ce3a3a — red (matches lifeCross.design)
    numberColor: 'rgb(255, 255, 255)', // white (modern)
    classicNumberColor: 'rgb(0, 0, 0)', // black (classic)
    classicNumberOutlineColor: 'rgb(255, 255, 255)', // white outline (classic)
    borderColor: 'rgb(33, 141, 240)' // #218df0 — same blue as primary
  },
  beneathTheLineStyle: 'classic',
  gateNumberAlign: 'right',
  gateNumberFontSize: 'medium',
  baseRotation: 'lps',
  undefinedStyle: 'inverse-border-hatch',
  undefinedOpacity: 0.55,
  undefinedBackgroundColor: 'rgb(253, 253, 253)',
  undefinedInnerBorderColor: 'rgb(255, 255, 255)',
  undefinedNotchColor: 'rgb(255, 255, 255)',
  undefinedHatchColor: 'rgba(255, 255, 255, 0.22)',
  nodeBackgroundStyle: 'side-color',
  exaltation: 'rgb(17, 242, 134)', // #11f286 — green
  exaltationArrow: 'rgb(50, 50, 50)', // #323232 — black
  detriment: 'rgb(50, 50, 50)', // #323232 — black
  detrimentArrow: 'rgb(17, 242, 134)', // #11f286 — green
  baseBadge: {
    defined: 'rgb(61, 65, 78)', // #3d414e — dark grey on dark blocks
    undefined: 'rgb(255, 255, 255)', // white on light blocks
    quadrantColor: 'rgb(206, 58, 58)' // #ce3a3a — design red
  },
  lineHighlight: {
    personality: 'rgb(189, 255, 0)', // #bdff00 — green-yellow (for cross blocks)
    design: 'rgb(255, 245, 0)' // #fff500 — yellow (for node blocks)
  },
  nodeMatchLineHighlight: {
    personality: 'rgb(255, 245, 0)', // #fff500 — yellow (design-side LPS matching personality node)
    design: 'rgb(255, 245, 0)' // #fff500 — yellow (personality-side LPS matching design node)
  },
  colorHighlight: {
    personality: 'rgb(189, 255, 0)', // #bdff00 — green-yellow
    design: 'rgb(220, 235, 179)' // #dcebb3 — muted green
  },
  colorHighlightDark: {
    personality: 'rgb(0, 135, 0)', // dark green
    design: 'rgb(80, 120, 0)' // dark muted green
  },
  lineHighlightDark: {
    personality: 'rgb(0, 135, 0)', // slightly brighter dark green for better visibility
    design: 'rgb(190, 150, 0)' // slightly brighter gold
  },
  nodeMatchLineHighlightDark: {
    personality: 'rgb(190, 150, 0)', // slightly brighter gold
    design: 'rgb(190, 150, 0)' // slightly brighter gold
  }
};

/**
 * Warm Dusk planet-grid palette. Same conceptual color slots as the Classic
 * config (black, red, the three blue tints, green, yellow, …) but every hue
 * is shifted to a warmer, duskier, slightly muted variant that aligns with
 * the Dusk Warm chrome (amber/terracotta accents on warm cream surface).
 * The functional meaning of each color stays identical to Classic.
 */
export const HD_PLANET_GRID_WARM_DUSK_COLOR_CONFIG: Readonly<HdPlanetGridColorConfig> = {
  incarnationCross: {
    personality: 'rgb(140, 75, 150)', // #8c4b96 — dusky violet (warmer, muted purple)
    design: 'rgb(140, 75, 150)' // #8c4b96 — dusky violet
  },
  lifeCross: {
    personality: 'rgb(35, 28, 25)', // #231c19 — warm near-black (brown-black)
    design: 'rgb(192, 80, 70)' // #c05046 — terracotta (warmer red)
  },
  lpsColumn: {
    presence: 'rgb(40, 90, 130)', // #285a82 — dusky teal (warmer dark blue)
    prana: 'rgb(70, 130, 175)', // #4682af — warm medium teal (warmer medium blue)
    vertical: 'rgb(40, 90, 130)' // #285a82 — dusky teal
  },
  lpsMoon: 'rgb(125, 165, 195)', // #7da5c3 — soft warm sky
  lpsMoonHighlighted: 'rgb(130, 182, 220)', // #82b6dc — lichtste blauw, maar genoeg verzadiging zodat het opgelicht (niet gedimd) leest
  colorToneBlock: 'rgb(220, 230, 235)', // #dce6eb — warm dusty sky
  colorToneShape: {
    primary: 'rgb(70, 130, 175)', // #4682af — warm medium teal (matches lpsColumn.prana)
    personality: 'rgb(35, 28, 25)', // #231c19 — warm near-black (matches lifeCross.personality)
    design: 'rgb(192, 80, 70)', // #c05046 — terracotta (matches lifeCross.design)
    numberColor: 'rgb(255, 255, 255)',
    classicNumberColor: 'rgb(0, 0, 0)',
    classicNumberOutlineColor: 'rgb(255, 255, 255)',
    borderColor: 'rgb(70, 130, 175)' // #4682af — same as primary
  },
  beneathTheLineStyle: 'classic',
  gateNumberAlign: 'right',
  gateNumberFontSize: 'medium',
  baseRotation: 'lps',
  undefinedStyle: 'inverse-border-hatch',
  undefinedOpacity: 0.55,
  undefinedBackgroundColor: 'rgb(249, 247, 241)', // #f9f7f1 — warm off-white (lichter dan vroegere #f8f4ec)
  undefinedInnerBorderColor: 'rgb(255, 255, 255)',
  undefinedNotchColor: 'rgb(255, 255, 255)',
  undefinedHatchColor: 'rgba(255, 255, 255, 0.22)',
  nodeBackgroundStyle: 'side-color',
  exaltation: 'rgb(85, 175, 110)', // #55af6e — warm sage (warmer green)
  exaltationArrow: 'rgb(35, 28, 25)', // #231c19 — warm dark
  detriment: 'rgb(35, 28, 25)', // #231c19 — warm dark
  detrimentArrow: 'rgb(85, 175, 110)', // #55af6e — warm sage
  baseBadge: {
    defined: 'rgb(75, 65, 60)', // #4b413c — warm dark grey
    undefined: 'rgb(255, 255, 255)',
    quadrantColor: 'rgb(192, 80, 70)' // #c05046 — terracotta (matches design)
  },
  lineHighlight: {
    personality: 'rgb(218, 228, 90)', // #dae45a — lighter warm chartreuse (still warm-dusk, less dark)
    design: 'rgb(245, 215, 95)' // #f5d75f — lighter warm ochre/yellow
  },
  nodeMatchLineHighlight: {
    personality: 'rgb(245, 215, 95)', // #f5d75f — lighter warm ochre
    design: 'rgb(245, 215, 95)' // #f5d75f — lighter warm ochre
  },
  colorHighlight: {
    personality: 'rgb(218, 228, 90)', // #dae45a — lighter warm chartreuse
    design: 'rgb(225, 218, 175)' // #e1daaf — lighter warm muted ochre
  },
  colorHighlightDark: {
    personality: 'rgb(95, 110, 30)', // #5f6e1e — warm dark olive
    design: 'rgb(120, 100, 30)' // #78641e — warm dark olive-gold
  },
  lineHighlightDark: {
    personality: 'rgb(95, 110, 30)', // #5f6e1e — warm dark olive
    design: 'rgb(165, 130, 35)' // #a58223 — warm dark gold
  },
  nodeMatchLineHighlightDark: {
    personality: 'rgb(165, 130, 35)', // #a58223 — warm dark gold
    design: 'rgb(165, 130, 35)' // #a58223 — warm dark gold
  }
};

/**
 * Tussenvariant tussen Klassiek en Warm Dusk. Elke kleur ligt **dichter
 * bij Warm Dusk dan bij Klassiek** (≈70% Warm Dusk + 30% Klassiek) — een
 * minder uitgesproken warm-dusk gevoel zonder volledig terug te vallen
 * op de koudere Klassiek-tinten.
 */
export const HD_PLANET_GRID_WARM_DUSK_SUBTLE_COLOR_CONFIG: Readonly<HdPlanetGridColorConfig> = {
  incarnationCross: {
    personality: 'rgb(148, 69, 164)', // tussen #a838c3 (klassiek) en #8c4b96 (warm dusk)
    design: 'rgb(148, 69, 164)'
  },
  lifeCross: {
    personality: 'rgb(29, 24, 22)',
    design: 'rgb(196, 73, 66)'
  },
  lpsColumn: {
    presence: 'rgb(28, 90, 143)',
    prana: 'rgb(58, 131, 192)',
    vertical: 'rgb(28, 90, 143)'
  },
  lpsMoon: 'rgb(106, 164, 209)',
  lpsMoonHighlighted: 'rgb(125, 184, 230)', // #7db8e6 — lichtste blauw met genoeg verzadiging zodat het opgelicht (niet gedimd) leest
  colorToneBlock: 'rgb(215, 232, 241)',
  colorToneShape: {
    primary: 'rgb(58, 131, 192)',
    personality: 'rgb(29, 24, 22)',
    design: 'rgb(196, 73, 66)',
    numberColor: 'rgb(255, 255, 255)',
    classicNumberColor: 'rgb(0, 0, 0)',
    classicNumberOutlineColor: 'rgb(255, 255, 255)',
    borderColor: 'rgb(58, 131, 192)'
  },
  beneathTheLineStyle: 'classic',
  gateNumberAlign: 'right',
  gateNumberFontSize: 'medium',
  baseRotation: 'lps',
  undefinedStyle: 'inverse-border-hatch',
  undefinedOpacity: 0.55,
  undefinedBackgroundColor: 'rgb(252, 251, 248)', // #fcfbf8 — warm off-white, iets witter dan Warm Dusk
  undefinedInnerBorderColor: 'rgb(255, 255, 255)',
  undefinedNotchColor: 'rgb(255, 255, 255)',
  undefinedHatchColor: 'rgba(255, 255, 255, 0.22)',
  nodeBackgroundStyle: 'side-color',
  exaltation: 'rgb(65, 195, 117)',
  exaltationArrow: 'rgb(40, 35, 32)',
  detriment: 'rgb(40, 35, 32)',
  detrimentArrow: 'rgb(65, 195, 117)',
  baseBadge: {
    defined: 'rgb(71, 65, 65)',
    undefined: 'rgb(255, 255, 255)',
    quadrantColor: 'rgb(196, 73, 66)'
  },
  lineHighlight: {
    personality: 'rgb(209, 236, 63)',
    design: 'rgb(248, 224, 67)'
  },
  nodeMatchLineHighlight: {
    personality: 'rgb(248, 224, 67)',
    design: 'rgb(248, 224, 67)'
  },
  colorHighlight: {
    personality: 'rgb(209, 236, 63)',
    design: 'rgb(223, 223, 176)'
  },
  colorHighlightDark: {
    personality: 'rgb(67, 118, 21)',
    design: 'rgb(108, 106, 21)'
  },
  lineHighlightDark: {
    personality: 'rgb(67, 118, 21)',
    design: 'rgb(172, 136, 25)'
  },
  nodeMatchLineHighlightDark: {
    personality: 'rgb(172, 136, 25)',
    design: 'rgb(172, 136, 25)'
  }
};

/** Whale-kleuren, gelijk aan de centerpreset Whale (hd-bodygraph-color-config.ts). */
const WHALES_RED = 'rgb(123, 36, 28)'; // #7b241c — motor
const WHALES_NAVY = 'rgb(4, 53, 81)'; // #043551 — bewustzijn
const WHALES_AMBER = 'rgb(210, 173, 118)'; // #d2ad76 — expressie

/**
 * Whale: Warm Dusk met de whale-kleuren van Svetlana Gafurova
 * (Steven 2026-10-17):
 * - beide nodes in whale-rood, ook de zwarte personality-node, ongeacht de
 *   node-kleurensetting;
 * - LPS: de blauwschakering schuift één stap op. Het donkere dusk-blauw wordt
 *   navy, het lichte blauw (prana) wordt het donkere dusk-blauw; de maan
 *   blijft één stap lichter dan prana;
 * - incarnatiekruis in amber, met donkere tekst omdat amber te licht is voor
 *   wit en de geelgroene lijn-highlight.
 * De Color/Tone-vormen houden hun zwarte personality-helft zodat personality
 * en design daar te onderscheiden blijven.
 */
export const HD_PLANET_GRID_WHALES_OF_PERSONALITY_COLOR_CONFIG: Readonly<HdPlanetGridColorConfig> = {
  ...HD_PLANET_GRID_WARM_DUSK_COLOR_CONFIG,
  incarnationCross: {
    personality: WHALES_AMBER,
    design: WHALES_AMBER,
  },
  incarnationCrossTextTone: 'dark',
  lifeCross: {
    personality: WHALES_RED,
    design: WHALES_RED,
  },
  lpsColumn: {
    presence: WHALES_NAVY,
    prana: 'rgb(40, 90, 130)', // #285a82 — het donkere dusk-blauw
    vertical: WHALES_NAVY,
  },
  lpsMoon: 'rgb(70, 130, 175)', // #4682af — het vroegere prana-blauw
  lpsMoonHighlighted: 'rgb(125, 165, 195)', // #7da5c3 — lichter, leest als opgelicht
  colorToneShape: {
    ...HD_PLANET_GRID_WARM_DUSK_COLOR_CONFIG.colorToneShape,
    primary: 'rgb(40, 90, 130)', // volgt lpsColumn.prana
    design: WHALES_RED,
    borderColor: 'rgb(40, 90, 130)',
  },
  baseBadge: {
    ...HD_PLANET_GRID_WARM_DUSK_COLOR_CONFIG.baseBadge,
    quadrantColor: WHALES_RED,
  },
  // Donkere varianten staan op het lichte amber van het incarnatiekruis: dieper
  // dan Warm Dusk zodat de profiellijn en de kleur daar leesbaar blijven.
  lineHighlightDark: {
    personality: 'rgb(55, 80, 20)', // #375014 — diep olijfgroen
    design: 'rgb(120, 85, 15)', // #78550f — diep oker
  },
  colorHighlightDark: {
    personality: 'rgb(55, 80, 20)',
    design: 'rgb(95, 70, 20)', // #5f4614 — diep olijfgoud
  },
};

export const HD_PLANET_GRID_COLOR_CONFIG_PRESETS: readonly HdPlanetGridColorConfigPreset[] = [
  { id: 'classic', label: 'Classic', config: HD_PLANET_GRID_CLASSIC_COLOR_CONFIG },
  { id: 'warm-dusk-subtle', label: 'Warm Dusk Subtle', config: HD_PLANET_GRID_WARM_DUSK_SUBTLE_COLOR_CONFIG },
  { id: 'warm-dusk', label: 'Warm Dusk', config: HD_PLANET_GRID_WARM_DUSK_COLOR_CONFIG },
  { id: 'whales-of-personality', label: 'Whale', config: HD_PLANET_GRID_WHALES_OF_PERSONALITY_COLOR_CONFIG },
];

export const HD_PLANET_GRID_DEFAULT_COLOR_CONFIG: Readonly<HdPlanetGridColorConfig> = HD_PLANET_GRID_WARM_DUSK_COLOR_CONFIG;
